/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getSdk } from "@cosmos/hygraph-schema/dist/generated/resolvers.hygraph";
import { GraphQLClient } from "graphql-request";
import { GraphQLClientResponse } from "node_modules/graphql-request/build/legacy/helpers/types";

import type { RequestOptions } from "graphql-request";

type GraphQLClientRequestHeaders = RequestOptions["requestHeaders"];
export type SdkType = ReturnType<typeof getSdk>;
export type ContentTypeNames = keyof SdkType;

const mediaUrlPrefix = `https://${process.env.HYGRAPH_MEDIA_DOMAIN || process.env.NEXT_PUBLIC_HYGRAPH_MEDIA_DOMAIN}`;
interface HygraphExtensions {
	complexity?: {
		complexityTree: {
			actual_no_of_docs: number;
			children: {
				actual_cost: number;
				actual_no_of_docs: number;
				estimated_cost: number;
				estimated_no_of_docs: number;
				field_name: string;
				xpath: string;
			}[];
			estimated_no_of_docs: number;
		};
		total_actual_cost: number;
		total_actual_docs: number;
		total_estimated_cost: number;
		total_estimated_docs: number;
	};
}

function fetchWrap(input: string | URL | globalThis.Request, init?: RequestInit): Promise<Response> {
	return fetch(input, { ...init, cache: "no-store" });
}

const hygraph = new GraphQLClient(`${process.env.HYGRAPH_ENDPOINT}/${process.env.HYGRAPH_ENVIRONMENT}` || "", {
	credentials: "include",
	mode: "cors",
	headers: {
		Authorization: `Bearer ${process.env.HYGRAPH_TOKEN}`,
		"gcms-media-url-prefix": mediaUrlPrefix,
	},
	fetch: fetchWrap,

	responseMiddleware: (response: GraphQLClientResponse<unknown> | Error) => {
		if (response instanceof Error) {
			console.error("[GraphQL] ERROR", response.message);
		} else if (response.data) {
			const name = response.headers.get("gcms-operation-name");
			const hit = response.headers.get("gcms-cache");

			const ext: HygraphExtensions = response.extensions as HygraphExtensions;
			if (process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true") {
				console.log(
					`GRAPHQL [${name}] ${hit} request completed. ${
						ext?.complexity?.total_actual_cost ? `complexity_cost [${ext?.complexity?.total_actual_cost}]` : ""
					}`,
				);
			}
		}
	},
});

const contentServiceHygraph = new GraphQLClient(process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT || "", {
	credentials: "include",
	mode: "cors",
	headers: {
		"gcms-media-url-prefix": mediaUrlPrefix,
	},
	fetch: fetchWrap,
});

export interface HygraphTarget {
	endpoint: string;
	token?: string;
	ownerId?: string;
	locales?: string[];
}

/** Per-site Hygraph target registry. Populated at startup; looked up at
 *  request time via the x-cosmos-site-id header. */
const hygraphTargetRegistry = new Map<string, HygraphTarget>();

/** Token-only registry for getOrCreateClient (maps endpoint URL → token). */
const endpointTokenRegistry = new Map<string, string>();

/** Register a site's Hygraph target. Call once per site at startup. */
export function registerHygraphTarget(siteId: string, target: HygraphTarget): void {
	hygraphTargetRegistry.set(siteId, target);
	if (target.token) {
		endpointTokenRegistry.set(target.endpoint, target.token);
	}
}

/** @deprecated Use registerHygraphTarget instead. */
export function registerEndpointToken(endpoint: string, token: string): void {
	endpointTokenRegistry.set(endpoint, token);
}

/** Resolve the Hygraph target for the current request from the x-cosmos-site-id header.
 *  Returns undefined outside Server Component context. */
export async function resolveHygraphTarget(): Promise<HygraphTarget | undefined> {
	try {
		// eslint-disable-next-line @typescript-eslint/no-require-imports
		const { headers } = require("next/headers") as { headers: () => Promise<{ get: (k: string) => string | null }> };
		const h = await headers();
		const siteId = h.get("x-cosmos-site-id");
		if (siteId) return hygraphTargetRegistry.get(siteId);
	} catch {
		// Not in Server Component context — fall through
	}
	return undefined;
}

/** Cache of per-endpoint GraphQL clients for multi-site support */
const endpointClientCache = new Map<string, GraphQLClient>();

function getOrCreateClient(endpoint: string): GraphQLClient {
	const cached = endpointClientCache.get(endpoint);
	if (cached) return cached;

	const headers: Record<string, string> = {
		"gcms-media-url-prefix": mediaUrlPrefix,
	};

	const token = endpointTokenRegistry.get(endpoint);
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}

	const client = new GraphQLClient(endpoint, {
		credentials: "include",
		mode: "cors",
		headers,
		fetch: fetchWrap,
	});
	endpointClientCache.set(endpoint, client);
	return client;
}

const previewHygraph = new GraphQLClient(`${process.env.HYGRAPH_ENDPOINT}/${process.env.HYGRAPH_ENVIRONMENT}` || "", {
	credentials: "include",
	mode: "cors",
	headers: {
		Authorization: `Bearer ${process.env.HYGRAPH_PREVIEW_TOKEN}`,
		"gcms-media-url-prefix": mediaUrlPrefix,
	},
	fetch: fetchWrap,
});

export const isValidPreviewParam = (previewParam?: string): boolean => {
	if (previewParam && previewParam === process.env.HYGRAPH_PREVIEW_PARAM) {
		return true;
	}
	return false;
};

export const getHygraph = (previewParam?: string, endpoint?: string): GraphQLClient => {
	if (isValidPreviewParam(previewParam)) {
		console.warn("Using Hygraph PREVIEW");
		return previewHygraph;
	}
	if (endpoint) {
		return getOrCreateClient(endpoint);
	}
	if (process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT) {
		return contentServiceHygraph;
	}

	return hygraph;
};

export const getHygraphHeaders = (preview: string | undefined, endpoint?: string): GraphQLClientRequestHeaders => {
	const headers: GraphQLClientRequestHeaders | undefined = {};
	if (preview || endpoint || process.env.HYGRAPH_CONTENT_SERVICE_ENDPOINT) {
		headers["hyg-stale-while-revalidate"] = "0";
	} else {
		if (process.env.HYGRAPH_STALE_REVALIDATE) {
			headers["hyg-stale-while-revalidate"] = `${process.env.HYGRAPH_STALE_REVALIDATE}`;
		}
	}
	return headers;
};

export const getHygraphCosmosPageData = async <FunctionName extends ContentTypeNames>(
	contentType: FunctionName,
	payload: Parameters<SdkType[FunctionName]>[0],
	preview: string | undefined,
	endpoint?: string,
): Promise<ReturnType<SdkType[FunctionName]>> => {
	const resolvedEndpoint = endpoint || (await resolveHygraphTarget())?.endpoint;
	const headers: GraphQLClientRequestHeaders | undefined = getHygraphHeaders(preview, resolvedEndpoint);

	if (!payload) {
		throw new Error("Missing payload");
	}

	const inspectComplexity =
		process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true";
	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));

	const fn = sdk[contentType];
	const start = Date.now();
	const pageData = await fn(payload as any, {
		"x-inspect-complexity": inspectComplexity ? "true" : "false",
		...headers,
	});

	if (inspectComplexity) {
		console.log(`GRAPHQL TIMING [${contentType}] ${Date.now() - start}`);
	}

	return pageData as ReturnType<SdkType[FunctionName]>;
};

/**
 * Generic function to get Hygraph page data for any SDK type.
 * This eliminates code duplication across all hygraph-*.ts files.
 *
 * @param getSdk - The getSdk function from the specific schema package
 * @param contentType - The content type/function name to call
 * @param payload - The payload/parameters for the function
 * @param preview - Preview parameter for Hygraph
 * @returns Promise with the page data
 */
export const getHygraphPageData = async <
	TSdk extends Record<string, (...args: any[]) => Promise<any>>,
	FunctionName extends keyof TSdk,
>(
	getSdk: (client: GraphQLClient) => TSdk,
	contentType: FunctionName,
	payload: Parameters<TSdk[FunctionName]>[0],
	preview: string | undefined,
	endpoint?: string,
): Promise<Awaited<ReturnType<TSdk[FunctionName]>>> => {
	const resolvedEndpoint = endpoint || (await resolveHygraphTarget())?.endpoint;
	const headers: GraphQLClientRequestHeaders | undefined = getHygraphHeaders(preview, resolvedEndpoint);

	if (!payload) {
		throw new Error("Missing payload");
	}

	const inspectComplexity =
		process.env.HYGRAPH_INSPECT_COMPLEXITY === true || process.env.HYGRAPH_INSPECT_COMPLEXITY === "true";
	const sdk = getSdk(getHygraph(preview, resolvedEndpoint));

	const fn = sdk[contentType];
	const start = Date.now();
	const pageData = await fn(payload as any, {
		"x-inspect-complexity": inspectComplexity ? "true" : "false",
		...headers,
	});

	if (inspectComplexity) {
		console.log(`GRAPHQL TIMING [${String(contentType)}] ${Date.now() - start}`);
	}

	return pageData;
};
